import { EventType } from "@/analytics/analytics-events";
import {
  ApplyEditWaypointsChangesProperties,
  DiscardEditWaypointsChangesProperties,
} from "@/analytics/edit-waypoints-tool-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useDetectUnmountOrDeactivation } from "@/hooks/use-detect-unmount-or-deactivation";
import { useDialog } from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback } from "react";
import { useBeforeUnload } from "react-router-dom";
import {
  selectEditWaypointsAnalyticsStats,
  selectHasEditWaypointsUnsavedChanges,
  useApplyChangesInEditWaypointsStore,
  useEditWaypointsStore,
  useResetEditWaypointsStore,
} from "./edit-waypoints-store";

/**
 * Hook that should be used by all components using the edit waypoints store.
 * It will initialize the store when the tool activates, and notify the user about unsaved changes on unmount.
 *
 * @param isToolActive Whether the edit waypoints tool is active
 */
export function useDetectEditWaypointsMount(isToolActive: boolean): void {
  const resetEditWaypointsStore = useResetEditWaypointsStore();
  const applyChangesInEditWaypointsStore =
    useApplyChangesInEditWaypointsStore();

  const { createDialog } = useDialog();

  const { handleErrorWithToast } = useErrorHandlers();

  const showSaveDialog = useCallback(() => {
    Analytics.track(EventType.leaveEditWaypointsUnintentionally);

    createDialog({
      title: "Save changes?",
      content:
        "You have made changes to your waypoints and location markers that have not been saved. Do you want to save them before leaving this mode?",
      cancelText: "Discard",
      confirmText: "Save changes",
      onConfirm: async () => {
        Analytics.track<ApplyEditWaypointsChangesProperties>(
          EventType.applyEditWaypointsChanges,
          {
            via: "save changes dialog",
            ...selectEditWaypointsAnalyticsStats(
              useEditWaypointsStore.getState(),
            ),
          },
        );

        try {
          await applyChangesInEditWaypointsStore();

          return true;
        } catch (error) {
          handleErrorWithToast({
            title: "Could not apply changes",
            error,
          });
          // Re-throw to prevent closing the dialog and allow the user to try again
          throw error;
        }
      },
      onCancel: () => {
        Analytics.track<DiscardEditWaypointsChangesProperties>(
          EventType.discardEditWaypointsChanges,
          { via: "save changes dialog" },
        );

        resetEditWaypointsStore();
      },
    });
  }, [
    applyChangesInEditWaypointsStore,
    createDialog,
    handleErrorWithToast,
    resetEditWaypointsStore,
  ]);

  // Prevent the browser from closing if there are unsaved changes
  const onBeforeUnload = useCallback(
    (ev: BeforeUnloadEvent) => {
      if (
        selectHasEditWaypointsUnsavedChanges(useEditWaypointsStore.getState())
      ) {
        ev.preventDefault();
        // All browsers prevent custom messages, so we just have to pass an empty string here to get the dialog to appear.
        ev.returnValue = "";
        showSaveDialog();
      }
    },
    [showSaveDialog],
  );
  useBeforeUnload(onBeforeUnload);

  const onUnmountOrDeactivation = useCallback(() => {
    if (
      selectHasEditWaypointsUnsavedChanges(useEditWaypointsStore.getState())
    ) {
      if (
        selectHasEditWaypointsUnsavedChanges(useEditWaypointsStore.getState())
      ) {
        showSaveDialog();
      } else {
        resetEditWaypointsStore();
      }
    }
  }, [resetEditWaypointsStore, showSaveDialog]);
  useDetectUnmountOrDeactivation(isToolActive, onUnmountOrDeactivation);
}
